import React, {
  useState,
  useRef,
  memo,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import DragDrop from "./DragDrop";
import { useData } from "../contexts/DataContext";
import { useParams } from "react-router-dom";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItem,
  List,
  Paper,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Stack,
  Tooltip,
  Link,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import InlineFeedback from "./InlineFeedback";
import ExistingInstruments from "./ExistingInstruments";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import pdfTableExtractor from "../utilities/pdf-table-extractor";
import { Base64 } from "js-base64";
import YouTube from "react-youtube";
import "../css/youtube.css";
import logoWithText from "../img/Logo-04-min.svg";
import pattern from "../img/pattern.svg";

export default function Upload({
  appFileInfos,
  setAppFileInfos,
  executeMatch,
  existingInstruments,
  ReactGA,
  fullscreen,
  setFullscreen,
}) {
  const [loading, setLoading] = useState(false);
  const [parseError, setParseError] = useState(false);
  const [matchError, setMatchError] = useState(false);
  const [importFeedback, setImportFeedback] = useState();
  const [grouping] = useState("");
  const [expanded, setExpanded] = useState(false);
  const dirty = useRef(false);
  const localFileInfos = useRef();
  const history = useHistory();
  const { parse, getSharedInstrument } = useData();
  const { importId } = useParams();

  ReactGA.send({
    hitType: "pageview",
    page: "/",
    title: "Upload / Definition",
  });

  const fileInfos = useMemo(() => {
    return dirty.current ? localFileInfos.current || [] : appFileInfos || [];
  }, [appFileInfos]);

  const setFileInfos = useCallback(
    (fi, forceExpanded) => {
      console.log("setting local FI ", fi);
      dirty.current = dirty.current + 1;
      localFileInfos.current = fi;
      if (forceExpanded) {
        setExpanded(forceExpanded);
      } else if (
        fi.length &&
        !(expanded && fi.map((i) => i.instrument_id).includes(expanded))
      ) {
        setExpanded(fi[0].instrument_id);
      }
    },
    [expanded, setExpanded]
  );
  const syncFileInfos = useCallback(() => {
    console.log("syncing fileinfo");
    setAppFileInfos(localFileInfos.current);
    dirty.current = false;
  }, [setAppFileInfos]);

  useEffect(() => {
    if (importId) {
      // immediately rewite URL to prevent reimport loop trauma
      history.push("/");

      // define validation functions
      const importToModelDef = (imported) => {
        const isValidImport = (inst) => {
          const isQuestionValid = (q) => {
            return !!q.question_text;
          };
          return (
            !!inst.instrument_name &&
            !!inst.questions &&
            Array.isArray(inst.questions) &&
            inst.questions.every((q) => isQuestionValid(q))
          );
        };
        // make it an array if just a single instrument
        if (!Array.isArray(imported)) imported = [imported];

        // if valid import - adding to the existing fileinfos - App contains a beforeunload to stash file infors between loads within session.
        if (imported.every((inst) => isValidImport(inst))) {
          imported.map((i) => (i.instrument_id = "Imported_" + Math.random()));
          setFileInfos([...fileInfos].concat(imported));
          syncFileInfos();
          return imported.length;
        } else {
          return 0;
        }
      };
      toast.promise(
        new Promise((resolve, reject) => {
          if (importId.length > 20 && Base64.isValid(importId)) {
            console.log("decoded", Base64.decode(importId));
            // Support the whole instrument being presented as a base64 encoded instrument object - This will only work for small instruments but enhances privacy and speed
            try {
              let imported = JSON.parse(Base64.decode(importId));
              const nImported = importToModelDef(imported);
              if (nImported) {
                setTimeout(() => resolve(nImported), 250);
              } else {
                setTimeout(() => reject(0), 250);
              }
            } catch (e) {
              setTimeout(() => reject(e), 250);
            }
          } else {
            getSharedInstrument(importId).then((imported) => {
              const nImported = importToModelDef(imported);
              if (nImported) {
                resolve(nImported);
              } else {
                reject(0);
              }
            });
          }
        }),
        {
          pending: "Importing your instruments",
          success: "All your instruments have been imported",
          error: "Sorry we couldn't interpret that import!",
        }
      );
    }
  });

  function filesReceiver(fileList) {
    const files = Array.from(fileList);
    let frp = [];
    files.forEach((file) => {
      frp.push(
        new Promise((resolve, reject) => {
          const fileExt = file.name.split(".").pop().toLowerCase();
          const reader = new FileReader();
          reader.onload = () => {
            if (fileExt === "pdf") {
              const uint8array = Uint8Array.from(
                atob(reader.result.split(",")[1]),
                (c) => c.charCodeAt(0)
              );
              var typedarray = new Uint8Array(uint8array);

              pdfTableExtractor(typedarray)
                .then((tables) => {
                  resolve({
                    file_name: file.name,
                    file_type: file.name.split(".").pop().toLowerCase(),
                    content: reader.result,
                    tables: tables.pageTables,
                  });
                })
                .catch((e) => {
                  // PDF clientside parsing failed for some reason - defer to the server
                  console.log(e);
                  resolve({
                    file_name: file.name,
                    file_type: file.name.split(".").pop().toLowerCase(),
                    content: reader.result,
                  });
                });
            } else {
              resolve({
                file_name: file.name,
                file_type: file.name.split(".").pop().toLowerCase(),
                content: reader.result,
              });
            }
          };
          reader.onerror = (e) => {
            reject(e);
          };
          if (fileExt === "txt" || fileExt === "csv") {
            reader.readAsText(file);
          } else {
            reader.readAsDataURL(file);
          }
        })
      );
    });
    Promise.all(frp)
      .then(async (allFiles) => {
        // Parse ONE file per API call, in series (issue #61). A single
        // /text/parse call carrying many large PDFs times out: Tika can take
        // well over a minute per big or scanned PDF, and the old 15s timeout
        // with 3 retries then re-sent the whole batch while the server was
        // still working on the first attempt. One file per call keeps each
        // request inside the proxy limits, shows progress, and lets the good
        // files land even if one bad scan fails. The API caches parsed files
        // by content, so re-uploading an already-parsed file is instant.
        const progress = (i) =>
          allFiles.length === 1
            ? `Parsing ${allFiles[0].file_name} - this may take a while`
            : `Parsing ${i + 1} of ${allFiles.length}: ${allFiles[i].file_name} - this may take a while`;
        const toastId = toast.loading(progress(0));
        const newFileInfos = [...fileInfos];
        const failed = [];
        let parsedCount = 0;
        for (let i = 0; i < allFiles.length; i++) {
          toast.update(toastId, { render: progress(i) });
          try {
            const data = await parse([allFiles[i]]);
            if (Array.isArray(data) && data.length) {
              data.forEach((instrument) => {
                newFileInfos.push(instrument);
              });
              // Show each file as soon as it lands rather than at the end
              setFileInfos([...newFileInfos]);
              parsedCount++;
            } else {
              failed.push(`${allFiles[i].file_name} (no questions found)`);
            }
          } catch (e) {
            console.log(e);
            failed.push(allFiles[i].file_name);
          }
        }
        syncFileInfos();
        if (failed.length === 0) {
          toast.update(toastId, {
            render: "Success!",
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
        } else {
          setParseError(true);
          toast.update(toastId, {
            render:
              parsedCount === 0
                ? "Something went wrong - please try again"
                : `Parsed ${parsedCount} of ${allFiles.length} files - could not parse ${failed.join(", ")}`,
            type: parsedCount === 0 ? "error" : "warning",
            isLoading: false,
            autoClose: 8000,
          });
        }
      })
      .catch((e) => {
        console.log(e);
      });
  }

  function existingReceiver(selected) {
    var newFileInfos = [...fileInfos];
    // Add the selected instruments to the infoList - attaching a instrument grouping key if needed
    // work through the current total selection and ensure any existing ones are in out out as required.
    const existingInstrumentIDs = existingInstruments.map((ei) => {
      return ei.instrument_id;
    });

    // trim out any which are existing instruments but not selected
    newFileInfos = newFileInfos
      .map((fi) => {
        if (
          (existingInstrumentIDs.includes(fi.instrument_id) &&
            selected.includes(fi.instrument_name)) ||
          !existingInstrumentIDs.includes(fi.instrument_id)
        ) {
          return fi;
        } else {
          return null;
        }
      })
      .filter((i) => {
        return i !== null;
      });

    // add in any selected which are not already in there
    selected.forEach((sin) => {
      let si = existingInstruments.filter((inst) => {
        return inst.instrument_name === sin;
      })[0];
      si.grouping = grouping;
      if (
        newFileInfos.filter((inst) => {
          return inst.instrument_id === si.instrument_id;
        }).length === 0
      ) {
        console.log("adding " + si.instrument_name);
        newFileInfos.push(si);
      }
    });

    setFileInfos(newFileInfos);
    syncFileInfos();
  }
  const updateInstrument = (instrument_id, value) => {
    console.log("updating instrument " + instrument_id);
    const newFileInfos = fileInfos.map((fileInfo) => {
      if (fileInfo.instrument_id === instrument_id) {
        fileInfo.instrument_name = value;
      }
      return fileInfo;
    });
    setFileInfos(newFileInfos);
  };
  const removeInstrument = (instrument_id) => {
    const newFileInfos = fileInfos.filter((fileInfo) => {
      return fileInfo.instrument_id !== instrument_id;
    });
    setFileInfos(newFileInfos);
    syncFileInfos();
  };

  const insertInstrument = (after_instrument_id) => {
    const after = fileInfos.findIndex(
      (f) => f.instrument_id === after_instrument_id
    );
    const instrument_id = "ManuallyCreated" + String(new Date().getTime());
    const newFileInfos = fileInfos
      .slice(0, after + 1)
      .concat([
        {
          instrument_id: instrument_id,
          instrument_name: "",
          questions: [{ question_no: "", question_text: "" }],
        },
      ])
      .concat(fileInfos.slice(after + 1));
    setFileInfos(newFileInfos, instrument_id);
    syncFileInfos();
  };

  const insertQuestion = (instrument_id, after_question_index) => {
    const instrument = fileInfos.filter((f) => {
      return f.instrument_id === instrument_id;
    })[0];

    const questions = instrument.questions
      .slice(0, after_question_index + 1)
      .concat([{ question_no: "", question_text: "" }])
      .concat(instrument.questions.slice(after_question_index + 1));

    const newFileInfos = fileInfos.map((fileInfo) => {
      if (fileInfo.instrument_id === instrument_id) {
        fileInfo.questions = questions;
      }
      return fileInfo;
    });
    setFileInfos(newFileInfos);
  };

  const removeQuestion = (instrument_id, question_index) => {
    const newFileInfos = fileInfos.map((fileInfo) => {
      if (fileInfo.instrument_id === instrument_id) {
        fileInfo.questions.splice(question_index, 1);
      }
      return fileInfo;
    });
    setFileInfos(newFileInfos);
  };

  const updateQuestion = (
    instrument_id,
    question_index,
    new_question_no,
    new_question_text
  ) => {
    console.log("updating Q");
    const newFileInfos = fileInfos.map((fileInfo) => {
      if (fileInfo.instrument_id === instrument_id) {
        if (new_question_no)
          fileInfo.questions[question_index].question_no = new_question_no;
        if (new_question_text)
          fileInfo.questions[question_index].question_text = new_question_text;
      }
      return fileInfo;
    });
    setFileInfos(newFileInfos);
  };

  const getQuestion = (instrument_id, index) => {
    return fileInfos.filter((fi) => {
      return fi.instrument_id === instrument_id;
    })[0].questions[index];
  };

  const QuestionDetail = memo(
    ({ question, index, instrument_id, rerender }) => {
      return (
        <ListItem
          key={instrument_id + "_" + index + "_" + question.question_no}
          sx={{ alignItems: "flex-end", pr: 0, mr: 0 }}
        >
          <TextField
            //setting the key to ensure the whole box is rerendered when the value changes
            key={instrument_id + "_" + index + "_" + question.question_no}
            sx={{ width: 35, px: 0, mx: 0 }}
            variant={"standard"}
            defaultValue={getQuestion(instrument_id, index).question_no}
            placeholder={question.question_no ? "" : "#"}
            onBlur={(e) => {
              updateQuestion(instrument_id, index, e.target.value);
              rerender((prev) => (prev || 0) + 1);
            }}
            onClickCapture={(e) => {
              e.stopPropagation();
            }}
          />
          <TextField
            multiline
            //setting the key to ensure the whole box is rerendered when the value changes
            key={instrument_id + "_" + index + question.question_text}
            sx={{ width: "100%", px: 1 }}
            variant={"standard"}
            defaultValue={getQuestion(instrument_id, index).question_text}
            placeholder={"Enter your question text"}
            onBlur={(e) => {
              updateQuestion(instrument_id, index, null, e.target.value);
              rerender((prev) => (prev || 0) + 1);
            }}
            onClickCapture={(e) => {
              e.stopPropagation();
            }}
          />
          <Stack
            direction="row"
            spacing={0}
            sx={{ pointerEvents: "none", mx: 0, right: 0 }}
          >
            <IconButton
              sx={{ pointerEvents: "auto" }}
              aria-label="delete"
              title="Delete this question"
              onClickCapture={(e) => {
                e.stopPropagation();
                removeQuestion(instrument_id, index);
                rerender((prev) => (prev || 0) + 1);
              }}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              sx={{ pointerEvents: "auto", pr: 0.7 }}
              aria-label="add-new"
              title="Add new question here"
              onClickCapture={(e) => {
                e.stopPropagation();
                insertQuestion(instrument_id, index);
                rerender((prev) => (prev || 0) + 1);
              }}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Stack>
        </ListItem>
      );
    }
  );

  const FileInfo = ({ fi }) => {
    const instrument_id = fi.instrument_id;
    const fileInfo = fi;
    const [, rerender] = useState();
    console.log("rendering " + instrument_id + fileInfo.instrument_name);
    return (
      <Accordion
        id={instrument_id}
        key={instrument_id}
        expanded={expanded === instrument_id}
        onChange={(e, ex) => {
          if (ex) setExpanded(instrument_id);
          syncFileInfos();
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon
              sx={{
                pointerEvents: "auto",
              }}
            />
          }
          sx={{
            pointerEvents: "none",
            flexDirection: "row",
            alignItems: "center",
            "& .MuiAccordionSummary-content": {
              justifyContent: "space-between",
              alignItems: "center",
              mr: 0.5,
            },
          }}
        >
          <TextField
            error={
              fi.questions.reduce((a, q) => (a = a + q.question_text), "")
                .length === 0
            }
            helperText={
              fi.questions.reduce((a, q) => (a = a + q.question_text), "")
                .length === 0
                ? "You must add questions before this will be harmonised"
                : false
            }
            variant="standard"
            sx={{
              pointerEvents: "auto",
              width: "100%",
              pr: 1,
              focusVisibleClassName: "NA",
            }}
            defaultValue={fileInfo.instrument_name}
            placeholder={"Manually create your instrument - add a name"}
            onClickCapture={(e) => {
              e.stopPropagation();
            }}
            onFocus={(e) => {
              console.log("entered  " + instrument_id);
              setExpanded(instrument_id);
            }}
            onBlur={(e) => {
              updateInstrument(instrument_id, e.target.value);
              rerender((i) => i || 0 + 1);
            }}
          />
          <IconButton
            aria-label="delete"
            onClickCapture={(e) => {
              e.stopPropagation();
              removeInstrument(instrument_id);
            }}
            sx={{
              pointerEvents: "auto",
            }}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            aria-label="add-new"
            onClickCapture={(e) => {
              e.stopPropagation();
              insertInstrument(instrument_id);
            }}
            sx={{
              pointerEvents: "auto",
            }}
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </AccordionSummary>
        {expanded && expanded === instrument_id && (
          <AccordionDetails
            sx={{
              width: "100%",
              marginTop: -3,
              marginLeft: -2,
              maxHeight: "50vh",
              overflowY: "auto",
            }}
          >
            {fileInfo.questions && fileInfo.questions.length && (
              <List dense={true}>
                {fileInfo.questions.map((question, i) => {
                  return (
                    <QuestionDetail
                      key={instrument_id + "_" + i}
                      question={question}
                      index={i}
                      instrument_id={instrument_id}
                      rerender={rerender}
                    />
                  );
                })}
              </List>
            )}
          </AccordionDetails>
        )}
      </Accordion>
    );
  };

  return (
    <Paper
      elevation={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        padding: "1rem",
      }}
    >
      {/* Intro Content */}
      <Box
        sx={{
          background: "linear-gradient(-135deg,#27EDB9, #2E5FFF)",
          backgroundImage: `linear-gradient(-135deg,#27EDB9DD, #2E5FFFAA), url(${pattern}), linear-gradient(-135deg,#27EDB9, #2E5FFF)`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          borderRadius: 2,
          padding: "2rem",
          color: "white",
          mb: 3,
        }}
      >
        <Box sx={{ width: "80%", maxWidth: 700, mx: "auto", mb: 3 }}>
          <img
            src={logoWithText}
            alt="Harmony Logo"
            style={{ width: "100%" }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ color: "white", mb: 2 }}>
            Harmonise questionnaire items
          </Typography>
          <Typography variant="body1" sx={{ color: "white", mb: 2 }}>
            Harmony is an AI tool which can read questionnaires and find
            questions with similar meanings, such as <i>anxiety</i> vs{" "}
            <i>I feel anxious</i>.
          </Typography>
          <Typography variant="body1" sx={{ color: "white", mb: 2 }}>
            Psychologists sometimes need to combine survey results, especially
            when surveys have been run by different organisations or in
            different countries.
          </Typography>
          <Typography variant="body1" sx={{ color: "white", mb: 2 }}>
            Try two example PDFs:{" "}
            <Link
              target="gad7-pdf"
              sx={{ color: "white" }}
              href="https://adaa.org/sites/default/files/GAD-7_Anxiety-updated_0.pdf"
            >
              GAD-7 PDF
            </Link>{" "}
            vs{" "}
            <Link
              target="phq-pdf"
              sx={{ color: "white" }}
              href="https://www.apa.org/depression-guideline/patient-health-questionnaire.pdf"
            >
              PHQ-9 PDF
            </Link>
            .
          </Typography>
          <Typography variant="body2" sx={{ color: "white" }}>
            <Link
              sx={{ color: "white" }}
              href="https://harmonydata.ac.uk/frequently-asked-questions"
            >
              FAQs
            </Link>{" "}
            -{" "}
            <Link
              sx={{ color: "white" }}
              href="https://harmonydata.ac.uk/privacy-policy"
            >
              Privacy policy
            </Link>{" "}
            -{" "}
            <Link
              sx={{ color: "white" }}
              href="https://harmonydata.ac.uk/formatting-help/"
            >
              Help with formatting
            </Link>{" "}
            -{" "}
            <Link
              sx={{ color: "white" }}
              href="https://harmonydata.ac.uk/troubleshooting-harmony/"
            >
              Troubleshooting
            </Link>
          </Typography>
        </Box>

        <YouTube
          className={"youtubeContainer" + (fullscreen ? "Full" : "")}
          iframeClassName="youtubeIframe"
          videoId="cEZppTBj1NI"
          onPlay={() => setFullscreen(true)}
          onPause={() => setFullscreen(false)}
        />
      </Box>

      <ToastContainer />
      <InlineFeedback
        message="The file could not be parsed"
        severity="error"
        state={parseError}
        setState={setParseError}
      />
      <InlineFeedback
        message="The match proceedure failed"
        severity="error"
        state={matchError}
        setState={setMatchError}
      />
      <InlineFeedback
        message={importFeedback}
        severity="info"
        state={!!importFeedback}
        setState={setImportFeedback}
      />
      <DragDrop filesReceiver={filesReceiver} sx={{ mt: "2rem" }} />
      <Stack
        direction={"row"}
        spacing={1}
        sx={{
          mt: 2,
          mr: 1,
          display: "flex",
          alignItems: "center",
          maxWidth: "100%",
        }}
      >
        <ExistingInstruments
          sx={{ width: "100%", flexShrink: 2 }}
          existingReceiver={existingReceiver}
          existingInstruments={existingInstruments}
          fileInfos={fileInfos}
        />
        <Tooltip title="Type in an instrument">
          <IconButton
            aria-label="add-new"
            onClick={(e) => {
              e.stopPropagation();
              insertInstrument();
            }}
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ marginTop: "1rem" }}>
        {fileInfos && fileInfos.length
          ? fileInfos.map((fi, i) => {
              return <FileInfo key={fi.instrument_id} fi={fi}></FileInfo>;
            })
          : ""}
      </Box>
      <Button
        variant="contained"
        size="large"
        sx={{ margin: "2rem" }}
        disabled={!fileInfos || fileInfos.length === 0 || loading}
        onClick={() => {
          console.log("executeMatch", executeMatch);
          setLoading(true);
          executeMatch()
            .then((_) => {
              history.push("/model");
              setLoading(false);
            })
            .catch((e) => {
              console.log(e);
              setMatchError(true);
            });
          /*  match(fileInfos)
            .then((data) => {
              let simpleApi = simplifyApi(data, fileInfos);
              setApiData(simpleApi);
              console.log(simpleApi);
              setLoading(false);
              history.push("/model");
            })
            .catch((e) => {
              console.log(e);
              setMatchError(true);
            }); */
        }}
      >
        {!loading && <Typography>Harmonise</Typography>}
        {loading && <CircularProgress />}
      </Button>
    </Paper>
  );
}
